import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { theme } from "../../Theme";
import ScenarioTable from "./components/ScenarioTable";
import BrandSummaryStrip from "./components/BrandSummaryStrip";
import {
  PROMO_ALL_BRANDS_ID,
  PROMO_DEFAULT_MANUFACTURER,
} from "./services/promoSimulatorService";

function filterScenariosForPi(scenarios, { brand, manufacturer }) {
  const list = Array.isArray(scenarios) ? scenarios : [];
  const mfr = manufacturer ?? PROMO_DEFAULT_MANUFACTURER;
  return list.filter((s) => {
    if (brand && brand !== PROMO_ALL_BRANDS_ID && s.brand !== brand) {
      return false;
    }
    if (mfr === PROMO_DEFAULT_MANUFACTURER) {
      if (s.manufacturer && s.manufacturer !== PROMO_DEFAULT_MANUFACTURER) {
        return false;
      }
    } else if (mfr && s.manufacturer && s.manufacturer !== mfr) {
      return false;
    }
    return true;
  });
}

export default function PromoSimulatorDashboard({ onSwitchTab, filters }) {
  const scenarios = useSelector((state) => state.promoSimulator.scenarios);

  const visibleScenarios = useMemo(
    () =>
      filterScenariosForPi(scenarios, {
        brand: filters?.brand,
        manufacturer: filters?.manufacturer ?? PROMO_DEFAULT_MANUFACTURER,
      }),
    [scenarios, filters?.brand, filters?.manufacturer]
  );

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 3,
          borderRadius: 2,
          border: "1px solid #E8ECF0",
          background:
            "linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, #FFFFFF 48%)",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.05)",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2.5}
        >
          <Box sx={{ maxWidth: 560 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              color={theme.palette.primary.main}
              sx={{ letterSpacing: "-0.02em", lineHeight: 1.25 }}
            >
              Promo Simulator Overview
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, lineHeight: 1.55 }}
            >
              Manage and track your promotional scenarios
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => onSwitchTab && onSwitchTab(1)}
            sx={{
              backgroundColor: theme.palette.primary.main,
              borderRadius: 2,
              textTransform: "none",
              whiteSpace: "nowrap",
              px: 2.25,
              py: 1,
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(25, 118, 210, 0.28)",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: "0 4px 14px rgba(25, 118, 210, 0.35)",
                transform: "translateY(-1px)",
              },
            }}
          >
            New Scenario
          </Button>
        </Box>
      </Paper>

      <BrandSummaryStrip scenarios={visibleScenarios} />

      <Typography
        variant="subtitle1"
        fontWeight={700}
        mb={1.5}
        color={theme.palette.primary.main}
        sx={{ letterSpacing: "-0.01em" }}
      >
        Saved scenarios
      </Typography>

      <ScenarioTable scenarios={visibleScenarios} />
    </Box>
  );
}
